import { useState } from 'react';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('phone_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      form.reset();
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }

    setFormStatus('submitting');
    setFormError('');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9sr27d8o1vn02ekukp0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 3000);
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试';
        setFormError(serverMsg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络后重试');
      setFormStatus('error');
    }
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'po文网',
    url: 'https://arkfilm.cn',
    description: '原创文章发布与内容创作平台',
    foundingDate: '2024',
    foundingLocation: '北京',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'contact@arkfilm.cn',
      telephone: '+86-400-888-0000',
    },
    sameAs: [
      'https://weibo.com/powenwang',
      'https://www.zhihu.com/org/powenwang',
    ],
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '如何在po文网发布文章？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '注册po文网账号后，进入创作中心，使用Markdown或富文本编辑器撰写文章，编辑完成后点击发布即可。文章发布后会经过AI原创检测，确保内容质量。',
        },
      },
      {
        '@type': 'Question',
        name: 'po文网对文章有什么要求？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'po文网鼓励原创内容，文章需为作者独立创作。内容应积极健康，不涉及违法、违规信息。平台支持科技、生活、文化、职场、旅行、美食、健康、教育等多种类型。',
        },
      },
      {
        '@type': 'Question',
        name: 'po文网如何保护原创作者的权益？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'po文网采用AI驱动的原创检测系统，对所有发布文章进行原创性审核。同时，我们为创作者提供版权保护工具，帮助创作者维护知识产权。',
        },
      },
      {
        '@type': 'Question',
        name: 'po文网的读者如何发现感兴趣的文章？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '读者可以通过分类浏览、关键词搜索和个性化推荐来发现感兴趣的文章。我们的智能推荐算法会根据读者的阅读偏好，推送最相关的内容。',
        },
      },
    ],
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://arkfilm.cn' },
      { '@type': 'ListItem', position: 2, name: '关于我们' },
    ],
  };

  return (
    <>
      <SEOHead
        title="关于我们"
        description="了解po文网——原创文章发布与内容创作平台。品牌介绍、用户口碑、联系方式。po文网汇聚海量优质创作者，致力于打造最好的中文写作社区。"
        keywords="po文网,关于po文网,po文网介绍,po文网联系方式,写作社区"
        canonicalUrl="https://arkfilm.cn/about"
        schema={orgSchema}
      />
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>

      <div className="min-h-screen pt-16">
        {/* Breadcrumb */}
        <div className="bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
            <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />
            <h1 className="font-heading text-2xl md:text-4xl text-foreground-900 mt-4 mb-2">
              关于 po文网
            </h1>
            <p className="text-sm md:text-base text-foreground-600">
              原创文章发布与内容创作平台
            </p>
          </div>
        </div>

        {/* Brand Story */}
        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-5">
                  我们的故事
                </h2>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  po文网诞生于2024年，由一群热爱写作与阅读的互联网人创立。我们发现，在信息爆炸的时代，优质原创内容反而越来越稀缺。太多好文章淹没在算法的洪流中，太多创作者的声音没有被听见。
                </p>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  我们的使命很简单：让每一篇好文章都被看见。给创作者提供一个纯净、专业的写作与分享空间，给读者提供一个发现优质内容的入口。
                </p>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                  上线至今，po文网已汇聚超过8,920位活跃创作者，累计发布12,580多篇原创文章，月活跃读者超过350万人次。但我们深知，这只是开始。
                </p>
              </div>
              <div className="aspect-[4/3] rounded-lg overflow-hidden bg-background-200">
                <img
                  src="https://readdy.ai/api/search-image?query=Creative%20team%20brainstorming%20in%20modern%20office%20with%20postit%20notes%20on%20whiteboard%20warm%20natural%20lighting%20collaborative%20atmosphere%20minimalist%20design%20editorial%20photography&width=800&height=600&seq=about-story&orientation=landscape"
                  alt="po文网团队"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Core Values */}
        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-3">
                我们的理念
              </h2>
              <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto">
                三个核心信念驱动着po文网的每一次迭代
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                {
                  icon: 'ri-lightbulb-flash-line',
                  title: '内容为王',
                  desc: '我们相信好内容自带生命力。不追逐流量热点，不鼓励标题党，算法服务于内容，而不是反过来。每一篇用心创作的文章都值得被认真对待。',
                },
                {
                  icon: 'ri-user-voice-line',
                  title: '创作者第一',
                  desc: '创作者是平台的核心资产。我们持续优化发文体验、提供数据洞察工具、保护原创权益，让创作者能专注于创作本身，而非流量焦虑。',
                },
                {
                  icon: 'ri-group-line',
                  title: '社区共建',
                  desc: 'po文网不仅是发布平台，更是一个写作社区。我们鼓励读者与作者之间、作者与作者之间的深度交流，让创作不再是一件孤独的事。',
                },
              ].map((item, idx) => (
                <div key={idx} className="text-center p-6 rounded-lg bg-background-50">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mx-auto mb-5">
                    <i className={`${item.icon} text-2xl`}></i>
                  </div>
                  <h3 className="font-heading text-lg text-foreground-900 mb-3">{item.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-14 md:py-20 px-4 md:px-6 bg-primary-500">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {[
                { num: '12,580+', label: '原创文章' },
                { num: '8,920+', label: '活跃创作者' },
                { num: '350万+', label: '月活跃读者' },
                { num: '98.6%', label: '读者满意度' },
              ].map((stat, idx) => (
                <div key={idx} className="text-center">
                  <div className="text-3xl md:text-4xl font-bold text-background-50 mb-2">{stat.num}</div>
                  <div className="text-sm text-background-50/80">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* User Testimonials */}
        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-3">
                创作者说
              </h2>
              <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto">
                来自真实创作者的声音，是po文网最好的口碑
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                {
                  name: '凌云',
                  role: '科技评论人',
                  quote: '在po文网写作两年了，最喜欢的不是流量数字，而是每篇文章下面那些认真、有深度的读者评论。这里有一种在别处感受不到的创作氛围。',
                  avatar: 'https://readdy.ai/api/search-image?query=Professional%20Chinese%20tech%20blogger%20portrait%20in%20warm%20neutral%20studio%20lighting%20with%20modern%20clean%20aesthetic%20and%20soft%20natural%20expression&width=200&height=200&seq=testimonial-lingyun&orientation=squarish',
                },
                {
                  name: '城南旧事',
                  role: '自由撰稿人',
                  quote: '用了很多写作平台，最后留在了po文网。编辑器的体验非常流畅，数据分析工具也让我更了解自己的读者。最重要的是，编辑团队真的很用心在推荐好内容。',
                  avatar: 'https://readdy.ai/api/search-image?query=Warm%20portrait%20of%20Chinese%20writer%20in%20cozy%20bookstore%20setting%20with%20soft%20warm%20lighting%20and%20natural%20expression&width=200&height=200&seq=testimonial-chengnan&orientation=squarish',
                },
                {
                  name: '知行客',
                  role: '教育博主',
                  quote: '作为一个兼职写作者，po文网给了我一个展示专业知识的舞台。通过平台的数据洞察，我不断优化自己的内容方向，从最初的几十个读者到现在上万粉丝。',
                  avatar: 'https://readdy.ai/api/search-image?query=Chinese%20education%20blogger%20portrait%20in%20library%20setting%20with%20books%20warm%20academic%20lighting%20thoughtful%20intellectual%20expression&width=200&height=200&seq=testimonial-zhixing&orientation=squarish',
                },
              ].map((testimonial, idx) => (
                <div key={idx} className="p-6 rounded-lg bg-background-100">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-full overflow-hidden bg-background-200 flex-shrink-0">
                      <img
                        src={testimonial.avatar}
                        alt={testimonial.name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                    <div>
                      <div className="font-medium text-sm text-foreground-900">{testimonial.name}</div>
                      <div className="text-xs text-foreground-500">{testimonial.role}</div>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed italic">
                    &ldquo;{testimonial.quote}&rdquo;
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-3">
                常见问题
              </h2>
              <p className="text-sm md:text-base text-foreground-600">
                关于po文网，你可能想了解的问题
              </p>
            </div>

            <div className="space-y-4">
              {[
                { q: '如何在po文网发布文章？', a: '注册po文网账号后，进入创作中心，使用Markdown或富文本编辑器撰写文章，编辑完成后点击发布即可。文章发布后会经过AI原创检测，确保内容质量。' },
                { q: 'po文网对文章有什么要求？', a: 'po文网鼓励原创内容，文章需为作者独立创作。内容应积极健康，不涉及违法、违规信息。平台支持科技、生活、文化、职场、旅行、美食、健康、教育等多种类型。' },
                { q: 'po文网如何保护原创作者的权益？', a: 'po文网采用AI驱动的原创检测系统，对所有发布文章进行原创性审核。同时，我们为创作者提供版权保护工具，帮助创作者维护知识产权。' },
                { q: 'po文网的读者如何发现感兴趣的文章？', a: '读者可以通过分类浏览、关键词搜索和个性化推荐来发现感兴趣的文章。我们的智能推荐算法会根据读者的阅读偏好，推送最相关的内容。' },
              ].map((item, idx) => (
                <details key={idx} className="group bg-background-50 rounded-lg border border-background-200">
                  <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                    <span className="font-medium text-sm md:text-base text-foreground-900">{item.q}</span>
                    <i className="ri-add-line text-lg text-foreground-500 group-open:hidden"></i>
                    <i className="ri-subtract-line text-lg text-foreground-500 hidden group-open:block"></i>
                  </summary>
                  <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                    {item.a}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-3">
                联系我们
              </h2>
              <p className="text-sm md:text-base text-foreground-600">
                有任何问题或建议？欢迎通过以下方式与我们取得联系
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
              {[
                { icon: 'ri-mail-line', title: '邮箱', content: 'contact@arkfilm.cn' },
                { icon: 'ri-phone-line', title: '电话', content: '400-888-0000' },
                { icon: 'ri-map-pin-line', title: '地址', content: '北京市海淀区中关村科技园' },
              ].map((contact, idx) => (
                <div key={idx} className="text-center p-4 rounded-lg bg-background-100">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mx-auto mb-3">
                    <i className={`${contact.icon} text-lg`}></i>
                  </div>
                  <div className="text-xs text-foreground-500 mb-1">{contact.title}</div>
                  <div className="text-sm text-foreground-900 font-medium">{contact.content}</div>
                </div>
              ))}
            </div>

            <form
              onSubmit={handleSubmit}
              data-readdy-form
              className="bg-background-100 rounded-lg p-6 md:p-8"
            >
              <h3 className="font-heading text-lg text-foreground-900 mb-6">发送消息</h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="contact-name" className="block text-sm text-foreground-700 mb-1.5">
                    姓名 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="contact-name"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all duration-200"
                    placeholder="请输入你的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm text-foreground-700 mb-1.5">
                    邮箱 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="contact-email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all duration-200"
                    placeholder="请输入你的邮箱"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label htmlFor="contact-subject" className="block text-sm text-foreground-700 mb-1.5">
                  主题 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="contact-subject"
                  name="subject"
                  required
                  className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all duration-200"
                  placeholder="请输入消息主题"
                />
              </div>

              <div className="mb-4">
                <label htmlFor="contact-message" className="block text-sm text-foreground-700 mb-1.5">
                  消息内容 <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  rows={5}
                  maxLength={500}
                  className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all duration-200 resize-none"
                  placeholder="请输入你的消息内容（最多500字）"
                ></textarea>
              </div>

              <input
                type="text"
                name="phone_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="honeypot-field"
              />

              {formStatus === 'error' && formError && (
                <div className="mb-4 p-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-700">
                  {formError}
                </div>
              )}

              {formStatus === 'success' && (
                <div className="mb-4 p-3 rounded-md bg-accent-50 border border-accent-200 text-sm text-accent-700">
                  消息已成功发送！我们会尽快回复你。
                </div>
              )}

              <button
                type="submit"
                disabled={formStatus === 'submitting'}
                className="w-full py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors duration-200 disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
              >
                {formStatus === 'submitting' ? '发送中...' : '发送消息'}
              </button>
            </form>
          </div>
        </section>
      </div>
    </>
  );
}